import { Link, type LinkProps } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type {
  ChatVisitorProfile,
  ChatVisitorVisit,
  LiveChatVisitor,
} from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@nucleus-commerce/ui/components/sheet';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useVisitorProfile } from '../hooks/use-visitor-profile';
import { summarizeUserAgent } from '../lib/user-agent';
import { minutesOnSite, SAFE_URL } from '../lib/visitor-display';

import type { StartChatTarget } from './start-chat-dialog';

type ThreadLink = (conversation: { id: string; storeId: string }) => LinkProps;

export interface VisitorDetailsTarget {
  storeId: string;
  visitorId: string;
  name: string;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2 border-b px-4 py-3 last:border-b-0">
      <h3 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">{title}</h3>
      {children}
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-3 text-sm">
      <dt className="text-muted-foreground w-28 shrink-0">{label}</dt>
      <dd className="min-w-0 flex-1 break-words">{children}</dd>
    </div>
  );
}

function displayName(kind: 'region' | 'language', code: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: kind }).of(code) ?? code;
  } catch {
    return code;
  }
}

function useDuration() {
  const { t } = useTranslation('conversations');
  return (minutes: number) =>
    minutes < 1
      ? t('visitors.duration.justNow')
      : minutes < 60
        ? t('visitors.duration.minutes', { count: minutes })
        : t('visitors.duration.hours', { hours: Math.floor(minutes / 60), minutes: minutes % 60 });
}

function PageTrail({ visit }: { visit: ChatVisitorVisit }) {
  const { t } = useTranslation('conversations');
  const format = useFormat();
  const duration = useDuration();
  const lastSeen = Date.parse(visit.lastSeenAt);
  return (
    <ol className="space-y-2.5" aria-label={t('visitorDetails.pagesLabel')}>
      {visit.pages.map((page, index) => {
        const newer = visit.pages[index - 1];
        // time on a page runs until the next one opened; the newest runs to the last heartbeat
        const until = newer ? Date.parse(newer.viewedAt) : lastSeen;
        const spent = Math.max(0, Math.floor((until - Date.parse(page.viewedAt)) / 60_000));
        const current = index === 0 && visit.live;
        const label = page.title?.trim() ? page.title : page.url;
        return (
          <li key={`${page.viewedAt}:${page.url}`} className="flex gap-3 text-sm">
            <time
              dateTime={page.viewedAt}
              title={format.utc(page.viewedAt)}
              className="text-muted-foreground w-12 shrink-0 pt-px text-xs tabular-nums"
            >
              {format.time(page.viewedAt)}
            </time>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {SAFE_URL.test(page.url) ? (
                  <a
                    href={page.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={page.url}
                    className="inline-flex min-w-0 items-center gap-1 underline-offset-2 hover:underline"
                  >
                    <span className="truncate">{label}</span>
                    <ExternalLink className="size-3 shrink-0" aria-hidden />
                  </a>
                ) : (
                  <span className="truncate">{label}</span>
                )}
                {current ? (
                  <Badge variant="secondary" className="shrink-0">
                    {t('visitorDetails.now')}
                  </Badge>
                ) : null}
              </div>
              <p className="text-muted-foreground truncate text-xs">
                {SAFE_URL.test(page.url) ? new URL(page.url).pathname : page.url}
                {current ? null : ` · ${duration(spent)}`}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Details({
  storeId,
  profile,
  live,
  threadLink,
  onClose,
}: {
  storeId: string;
  profile: ChatVisitorProfile;
  live: LiveChatVisitor | null;
  threadLink: ThreadLink;
  onClose: () => void;
}) {
  const { t } = useTranslation('conversations');
  const format = useFormat();
  const duration = useDuration();
  const { visit, customer } = profile;
  const agent = summarizeUserAgent(profile.userAgent);
  const country = profile.country ? displayName('region', profile.country, format.locale) : null;
  const place = [profile.city, profile.region, country].filter(Boolean).join(', ');
  const locale = live?.locale ?? profile.locale;
  const device = visit?.device ?? live?.device ?? null;
  const referrer = visit?.page.referrer ?? null;

  return (
    <div className="text-sm">
      <Section title={t('visitorDetails.where')}>
        <dl className="space-y-1">
          <Row label={t('visitorDetails.location')}>{place || '—'}</Row>
          <Row label={t('visitorDetails.ip')}>{profile.lastIp ?? '—'}</Row>
          <Row label={t('visitorDetails.language')}>
            {locale ? displayName('language', locale, format.locale) : '—'}
          </Row>
          <Row label={t('visitorDetails.browser')}>
            <span title={profile.userAgent ?? undefined}>{agent.browser ?? '—'}</span>
          </Row>
          <Row label={t('visitorDetails.system')}>{agent.system ?? '—'}</Row>
          <Row label={t('visitorDetails.device')}>
            {device ? t(`visitors.device.${device}`) : '—'}
          </Row>
        </dl>
      </Section>

      {visit ? (
        <Section title={t('visitorDetails.thisVisit')}>
          <dl className="space-y-1">
            <Row label={t('visitorDetails.started')}>
              <span title={format.utc(visit.startedAt)}>
                {t('visitorDetails.startedValue', {
                  time: format.time(visit.startedAt),
                  duration: duration(minutesOnSite(visit.startedAt, Date.parse(visit.lastSeenAt))),
                })}
              </span>
            </Row>
            <Row label={t('visitorDetails.pagesViewed')}>{visit.pageViews}</Row>
            <Row label={t('visitorDetails.cameFrom')}>
              {referrer && SAFE_URL.test(referrer) ? (
                <a
                  href={referrer}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="break-all underline-offset-2 hover:underline"
                >
                  {new URL(referrer).host}
                </a>
              ) : (
                t('visitorDetails.direct')
              )}
            </Row>
            <Row label={t('visitorDetails.cart')}>
              {visit.cartTotal ? format.money(visit.cartTotal) : t('visitorDetails.emptyCart')}
            </Row>
          </dl>
        </Section>
      ) : null}

      <Section
        title={
          visit
            ? t('visitorDetails.pagesVisited', { count: visit.pages.length })
            : t('visitorDetails.pagesVisitedNone')
        }
      >
        {visit && visit.pages.length > 0 ? (
          <PageTrail visit={visit} />
        ) : (
          <p className="text-muted-foreground text-sm">{t('visitorDetails.noVisit')}</p>
        )}
      </Section>

      <Section title={t('visitorDetails.history')}>
        <dl className="space-y-1">
          <Row label={t('visitorDetails.visits')}>{profile.visitsCount}</Row>
          <Row label={t('visitorDetails.firstSeen')}>
            <span title={format.utc(profile.firstSeenAt)}>{format.date(profile.firstSeenAt)}</span>
          </Row>
          <Row label={t('visitorDetails.lastSeen')}>
            <span title={format.utc(profile.lastSeenAt)}>
              {format.relative(visit?.lastSeenAt ?? profile.lastSeenAt)}
            </span>
          </Row>
        </dl>
      </Section>

      {customer ? (
        <Section title={t('context.customer')}>
          <dl className="space-y-1">
            <Row label={t('visitorDetails.name')}>
              <Link
                to="/s/$storeId/customers/$customerId"
                params={{ storeId, customerId: customer.id }}
                onClick={onClose}
                className="underline-offset-2 hover:underline"
              >
                {[customer.firstName, customer.lastName].filter(Boolean).join(' ') ||
                  customer.email}
              </Link>
            </Row>
            <Row label={t('context.email')}>{customer.email}</Row>
            <Row label={t('context.orders')}>{customer.ordersCount}</Row>
          </dl>
        </Section>
      ) : null}

      <Section title={t('visitorDetails.conversations', { count: profile.conversations.length })}>
        {profile.conversations.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('visitorDetails.noConversations')}</p>
        ) : (
          <ul className="space-y-2">
            {profile.conversations.map((conversation) => (
              <li key={conversation.id}>
                <Link
                  {...threadLink(conversation)}
                  onClick={onClose}
                  className="hover:bg-muted/60 -mx-2 block rounded-md px-2 py-1.5"
                >
                  <span className="flex items-center gap-2">
                    <span className="font-medium tabular-nums">#{conversation.number}</span>
                    <Badge variant="outline">{t(`status.${conversation.status}`)}</Badge>
                    <span className="text-muted-foreground ml-auto text-xs">
                      {format.date(conversation.lastMessageAt)}
                    </span>
                  </span>
                  {conversation.lastMessage ? (
                    <span className="text-muted-foreground mt-0.5 block truncate text-xs">
                      {conversation.lastMessage.preview}
                    </span>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

/**
 * tawk.to's visitor details: where the shopper is, their browser and device, this visit with
 * every page opened in order, earlier visits and conversations. Refetched every 30 s while open.
 */
export function VisitorDetailsSheet({
  target,
  live,
  threadLink,
  onStartChat,
  onOpenChange,
}: {
  target: VisitorDetailsTarget | null;
  /** The visitor's row in the live list, when they are still on it. */
  live: LiveChatVisitor | null;
  threadLink: ThreadLink;
  onStartChat: (target: StartChatTarget) => void;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('conversations');
  return (
    <Sheet open={target !== null} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 overflow-y-auto p-0 sm:max-w-md">
        {target ? (
          <SheetBody
            target={target}
            live={live}
            threadLink={threadLink}
            onStartChat={onStartChat}
            onClose={() => onOpenChange(false)}
          />
        ) : (
          <SheetHeader>
            <SheetTitle>{t('visitorDetails.title')}</SheetTitle>
          </SheetHeader>
        )}
      </SheetContent>
    </Sheet>
  );
}

function SheetBody({
  target,
  live,
  threadLink,
  onStartChat,
  onClose,
}: {
  target: VisitorDetailsTarget;
  live: LiveChatVisitor | null;
  threadLink: ThreadLink;
  onStartChat: (target: StartChatTarget) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('conversations');
  const profile = useVisitorProfile(target.storeId, target.visitorId);
  const visit = profile.data?.visit ?? null;
  const conversation = live?.conversation ?? null;
  const status = !visit?.live
    ? t('visitorDetails.left')
    : visit.visible
      ? t('visitorDetails.onSite')
      : t('visitorDetails.background');

  return (
    <>
      <SheetHeader className="border-b">
        <SheetTitle className="flex items-center gap-2">
          <span className="truncate">{target.name}</span>
          {profile.data?.customer ? (
            <Badge variant="secondary">{t('visitors.customer')}</Badge>
          ) : null}
          {profile.data?.banned ? (
            <Badge variant="destructive">{t('visitorDetails.banned')}</Badge>
          ) : null}
        </SheetTitle>
        <SheetDescription>{profile.data ? status : t('visitorDetails.title')}</SheetDescription>
        <div className="pt-2">
          {conversation ? (
            <Button asChild size="sm" variant="outline">
              <Link
                {...threadLink({ id: conversation.id, storeId: target.storeId })}
                onClick={onClose}
              >
                {t('visitors.openChat', { number: conversation.number })}
              </Link>
            </Button>
          ) : (
            <Can permission="conversations.write">
              <Button
                size="sm"
                disabled={profile.data?.banned === true}
                onClick={() => {
                  onStartChat({
                    storeId: target.storeId,
                    visitorId: target.visitorId,
                    name: target.name,
                  });
                  onClose();
                }}
              >
                {t('visitors.startChat')}
              </Button>
            </Can>
          )}
        </div>
      </SheetHeader>
      {profile.isPending ? (
        <div className="space-y-3 p-4">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-5 w-full" />
          ))}
        </div>
      ) : profile.error ? (
        <div className="p-4">
          <ErrorState error={profile.error} onRetry={() => void profile.refetch()} />
        </div>
      ) : (
        <Details
          storeId={target.storeId}
          profile={profile.data}
          live={live}
          threadLink={threadLink}
          onClose={onClose}
        />
      )}
    </>
  );
}
